import React from 'react';
import Footer from './Footer';
import LoginModal from './LoginModal';

type Props = {
  loading: boolean;
  error: string | null;
  onLogin: () => void;
  onEmailLinkLogin: (email: string) => Promise<void>;
};

const insightItems = [
  {
    icon: 'fa-solid fa-diagram-project',
    title: '検索ニーズを俯瞰',
    text: '複数サービスのサジェストから、ユーザーが本当に探している言葉をまとめて捉えます。',
    tone: 'bg-blue-50 text-blue-700',
  },
  {
    icon: 'fa-solid fa-chart-column',
    title: '需要の大きさを判断',
    text: '検索ボリュームと関連キーワードを重ねて、次に調べるべきテーマを絞り込みます。',
    tone: 'bg-emerald-50 text-emerald-700',
  },
  {
    icon: 'fa-solid fa-lightbulb',
    title: '施策の仮説まで進む',
    text: 'AIインサイトとペルソナを使い、言葉の奥にある悩みや購買動機を整理します。',
    tone: 'bg-amber-50 text-amber-700',
  },
];

const researchSteps = [
  ['01', 'キーワードを入力', '調べたい商品、悩み、テーマをひとつ入力します。'],
  ['02', '検索ニーズを発見', 'サジェスト、関連語、ネットワークから需要の輪郭を掴みます。'],
  ['03', '次の施策を決める', 'AI分析とペルソナを材料に、コンテンツや商品企画へつなげます。'],
];

const faqs = [
  ['どんな調査に向いていますか？', 'SEO、コンテンツ企画、商品開発、広告訴求、市場理解など、検索ニーズを起点にした調査に使えます。'],
  ['無料で試せますか？', 'はい。ログイン後、無料お試し枠ですぐに分析を始められます。'],
  ['どの検索サービスに対応していますか？', 'Google、Amazon、YouTube、楽天、TikTok、Bingなど、複数の検索サービスを切り替えて調査できます。'],
];

const LandingPage: React.FC<Props> = ({ loading, error, onLogin, onEmailLinkLogin }) => {
  const [loginOpen, setLoginOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <a href="/" aria-label="NeedRadar ホーム">
            <img src="/logo_2.png" alt="NeedRadar" className="h-9 w-auto" />
          </a>
          <nav className="hidden items-center gap-7 text-sm font-semibold text-slate-600 md:flex">
            <a href="#features" className="hover:text-blue-700">できること</a>
            <a href="#flow" className="hover:text-blue-700">使い方</a>
            <a href="#faq" className="hover:text-blue-700">よくある質問</a>
          </nav>
          <button
            type="button"
            onClick={() => setLoginOpen(true)}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700"
          >
            無料で試す
          </button>
        </div>
      </header>

      <main>
        <section className="relative min-h-[620px] overflow-hidden bg-slate-900 md:min-h-[calc(100vh-64px)]">
          <img
            src="/images/needradar-lp-hero.png"
            alt="NeedRadarで検索ニーズを分析するワークスペース"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/62 to-slate-900/5" />
          <div className="relative mx-auto flex min-h-[620px] max-w-7xl items-center px-5 pb-24 pt-16 sm:px-8 md:min-h-[calc(100vh-64px)]">
            <div className="max-w-2xl text-white">
              <p className="text-sm font-bold text-cyan-300">SEARCH NEEDS INTELLIGENCE</p>
              <h1 className="mt-5 text-4xl font-black leading-tight sm:text-5xl">
                検索の裏側にある、
                <br />
                本当のニーズを見つける。
              </h1>
              <p className="mt-6 max-w-xl text-base font-medium leading-8 text-slate-100 sm:text-lg">
                NeedRadarは、サジェスト検索、需要データ、AI分析をひとつにつなぐリサーチツール。
                顧客がまだ言葉にしきれていない悩みまで、施策に使える形で見つけます。
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setLoginOpen(true)}
                  className="rounded bg-blue-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-950/30 transition hover:bg-blue-500"
                >
                  無料で分析を始める
                  <i className="fa-solid fa-arrow-right ml-2" />
                </button>
                <a
                  href="#features"
                  className="rounded border border-white/40 bg-white/10 px-6 py-3 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20"
                >
                  できることを見る
                </a>
              </div>
              <p className="mt-4 text-xs font-semibold text-slate-300">クレジットカード不要 / 無料お試し枠あり</p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 border-t border-white/15 bg-slate-950/50 backdrop-blur">
            <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-5 py-4 text-xs font-bold text-slate-100 sm:grid-cols-4 sm:px-8">
              <p><i className="fa-solid fa-magnifying-glass mr-2 text-cyan-300" />複数サービス対応</p>
              <p><i className="fa-solid fa-chart-line mr-2 text-cyan-300" />需要データを可視化</p>
              <p><i className="fa-solid fa-brain mr-2 text-cyan-300" />AIインサイト</p>
              <p><i className="fa-solid fa-user-group mr-2 text-cyan-300" />ペルソナ生成</p>
            </div>
          </div>
        </section>

        <section id="features" className="border-b border-slate-200 bg-slate-50 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-2xl">
              <p className="text-sm font-bold text-blue-700">FEATURES</p>
              <h2 className="mt-3 text-3xl font-black text-slate-900 sm:text-4xl">見つける。測る。深掘る。</h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                点在していた調査をひとつの画面に集約。検索キーワードから、次の打ち手まで進めます。
              </p>
            </div>
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {insightItems.map((item) => (
                <article key={item.title} className="rounded border border-slate-200 bg-white p-6 shadow-sm">
                  <div className={`flex h-11 w-11 items-center justify-center rounded ${item.tone}`}>
                    <i className={item.icon} />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="overflow-hidden bg-white py-20 sm:py-24">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(520px,1.15fr)]">
            <div>
              <p className="text-sm font-bold text-emerald-700">ONE WORKSPACE</p>
              <h2 className="mt-3 text-3xl font-black leading-tight text-slate-900 sm:text-4xl">
                キーワード調査を、
                <br />
                意思決定まで止めない。
              </h2>
              <p className="mt-5 text-base leading-8 text-slate-600">
                関連語を集めるだけで終わらず、需要の大きさ、検索意図、ペルソナまで連続して確認できます。
                調査結果は履歴や保存キーワードとして残り、次の分析にも活かせます。
              </p>
              <div className="mt-7 flex flex-wrap gap-2 text-xs font-bold text-slate-700">
                {['ワードクラウド', 'ネットワーク表示', '検索ボリューム', '関連キーワード', 'AIインサイト', 'ペルソナ'].map((item) => (
                  <span key={item} className="rounded border border-slate-200 bg-slate-50 px-3 py-2">{item}</span>
                ))}
              </div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 shadow-xl shadow-slate-200/70">
              <div className="rounded border border-slate-200 bg-white">
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <img src="/logo_2.png" alt="" className="h-6 w-auto" />
                  <span className="rounded bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700">分析プレビュー</span>
                </div>
                <div className="grid gap-3 p-4 sm:grid-cols-[1.2fr_.8fr]">
                  <div className="rounded border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-bold text-slate-500">KEYWORD NETWORK</p>
                    <div className="relative mt-4 h-44">
                      <svg
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 h-full w-full"
                      >
                        <line x1="48" y1="48" x2="16" y2="24" stroke="#cbd5e1" strokeWidth="1.2" />
                        <line x1="54" y1="42" x2="81" y2="18" stroke="#cbd5e1" strokeWidth="1.2" />
                        <line x1="55" y1="55" x2="83" y2="76" stroke="#cbd5e1" strokeWidth="1.2" />
                        <line x1="43" y1="58" x2="24" y2="84" stroke="#cbd5e1" strokeWidth="1.2" />
                      </svg>
                      {[
                        ['キャンプ', 'left-[38%] top-[36%] bg-blue-600 text-white'],
                        ['初心者', 'left-[1%] top-[8%] bg-white text-blue-700'],
                        ['道具', 'right-[2%] top-[2%] bg-white text-emerald-700'],
                        ['家族', 'right-[0%] bottom-[6%] bg-white text-amber-700'],
                        ['冬', 'left-[11%] bottom-[0%] bg-white text-slate-700'],
                      ].map(([label, classes]) => (
                        <span key={label} className={`absolute z-10 rounded border border-slate-200 px-3 py-2 text-xs font-bold shadow-sm ${classes}`}>{label}</span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="rounded border border-slate-200 p-4">
                      <p className="text-xs font-bold text-slate-500">SEARCH VOLUME</p>
                      <p className="mt-3 text-2xl font-black text-slate-900">12,100</p>
                      <div className="mt-4 flex h-14 items-end gap-1">
                        {[28, 45, 38, 63, 54, 74, 68, 91].map((height, index) => (
                          <span key={index} className="flex-1 rounded-t bg-blue-500" style={{ height: `${height}%` }} />
                        ))}
                      </div>
                    </div>
                    <div className="rounded border border-emerald-200 bg-emerald-50 p-4">
                      <p className="text-xs font-bold text-emerald-700">AI INSIGHT</p>
                      <p className="mt-2 text-xs leading-5 text-emerald-900">初心者向けの道具選びと、家族で始める不安解消に需要があります。</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="flow" className="border-y border-slate-200 bg-slate-900 py-20 text-white sm:py-24">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <p className="text-sm font-bold text-cyan-300">HOW IT WORKS</p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">調査は、ひとつのキーワードから。</h2>
            <div className="mt-10 grid gap-px overflow-hidden rounded border border-slate-700 bg-slate-700 md:grid-cols-3">
              {researchSteps.map(([number, title, text]) => (
                <article key={number} className="bg-slate-900 p-6">
                  <p className="text-sm font-black text-cyan-300">{number}</p>
                  <h3 className="mt-5 text-xl font-bold">{title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-300">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-blue-50 py-20 sm:py-24">
          <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
            <p className="text-sm font-bold text-blue-700">START RESEARCH</p>
            <h2 className="mt-3 text-3xl font-black text-slate-900 sm:text-4xl">顧客の「探している」を、今日から見つける。</h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600">
              まずは無料お試し枠で、気になるキーワードをひとつ分析してみてください。
            </p>
            <button
              type="button"
              onClick={() => setLoginOpen(true)}
              className="mt-8 rounded bg-blue-600 px-7 py-3 text-sm font-bold text-white transition hover:bg-blue-700"
            >
              無料で分析を始める
              <i className="fa-solid fa-arrow-right ml-2" />
            </button>
          </div>
        </section>

        <section id="faq" className="bg-white py-20 sm:py-24">
          <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <p className="text-sm font-bold text-blue-700">FAQ</p>
            <h2 className="mt-3 text-3xl font-black text-slate-900">よくある質問</h2>
            <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
              {faqs.map(([question, answer]) => (
                <details key={question} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-sm font-bold text-slate-900">
                    {question}
                    <i className="fa-solid fa-plus text-blue-600 transition group-open:rotate-45" />
                  </summary>
                  <p className="mt-3 pr-8 text-sm leading-7 text-slate-600">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <LoginModal
        open={loginOpen}
        loading={loading}
        error={error}
        onClose={() => setLoginOpen(false)}
        onLogin={onLogin}
        onEmailLinkLogin={onEmailLinkLogin}
      />
    </div>
  );
};

export default LandingPage;
